const PREFERRED_FETCH_IMAGE_TYPES = new Set(['image/png', 'image/jpeg', 'image/jpg']);
const CLIPBOARD_SAFE_IMAGE_TYPE = 'image/png';

const blobToDataUrl = blob => new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = reject;
    reader.readAsDataURL(blob);
});

const convertBlobToPng = async blob => {
    if (!blob) {
        return null;
    }

    const dataUrl = await blobToDataUrl(blob);

    return new Promise((resolve, reject) => {
        const image = new window.Image();
        image.onload = () => {
            try {
                const canvas = document.createElement('canvas');
                canvas.width = image.naturalWidth || image.width;
                canvas.height = image.naturalHeight || image.height;
                const context = canvas.getContext('2d');
                context.drawImage(image, 0, 0);
                canvas.toBlob(pngBlob => {
                    if (pngBlob) {
                        resolve(pngBlob);
                        return;
                    }
                    reject(new Error('Failed to convert image to PNG'));
                }, 'image/png');
            } catch (error) {
                reject(error);
            }
        };
        image.onerror = reject;
        image.src = dataUrl;
    });
};

export const fetchPreferredImageBlob = async sourceChain => {
    const sources = Array.isArray(sourceChain) ? sourceChain.filter(Boolean) : [];
    let fallbackBlob = null;
    let fallbackSource = null;

    for (const source of sources) {
        try {
            const response = await fetch(source);
            if (!response.ok) {
                continue;
            }

            const blob = await response.blob();
            if (!blob?.type?.startsWith?.('image/')) {
                continue;
            }

            if (PREFERRED_FETCH_IMAGE_TYPES.has(blob.type.toLowerCase())) {
                return { blob, source };
            }

            if (!fallbackBlob) {
                fallbackBlob = blob;
                fallbackSource = source;
            }
        } catch (error) {
            // Try the next source.
        }
    }

    if (!fallbackBlob) {
        return null;
    }
    return { blob: fallbackBlob, source: fallbackSource };
};

const normalizeClipboardBlob = async blob => {
    if (!blob) {
        return null;
    }

    if (blob.type.toLowerCase() === CLIPBOARD_SAFE_IMAGE_TYPE) {
        return blob;
    }

    return convertBlobToPng(blob);
};

export const copyImageFromSourceChain = async sourceChain => {
    if (
        typeof navigator === 'undefined' ||
        !navigator.clipboard?.write ||
        typeof ClipboardItem === 'undefined'
    ) {
        return false;
    }

    // Start the clipboard write during the click; defer fetching/conversion through
    // ClipboardItem's promise so browsers retain the user gesture permission.
    const imageBlob = fetchPreferredImageBlob(sourceChain).then(async fetched => {
        const normalizedBlob = await normalizeClipboardBlob(fetched?.blob);
        if (!normalizedBlob?.type) throw new Error('Image unavailable');
        return normalizedBlob;
    });
    // A denied clipboard write may never consume the pending image promise.
    imageBlob.catch(() => {});
    await navigator.clipboard.write([
        new ClipboardItem({ [CLIPBOARD_SAFE_IMAGE_TYPE]: imageBlob })
    ]);
    return true;
};

const withBlobImage = async (blob, consume) => {
    const url = URL.createObjectURL(blob);
    const image = new window.Image();
    try {
        await new Promise((resolve, reject) => {
            image.onload = resolve;
            image.onerror = reject;
            image.src = url;
        });
        return consume(image);
    } finally {
        image.onload = null;
        image.onerror = null;
        image.src = '';
        URL.revokeObjectURL(url);
    }
};

export const buildCombinedImageBlob = async images => {
    if (!images.length) throw new Error('No images');
    const tiles = [];
    const gap = 16;
    let width = 0;
    let height = 0;
    let canvas;
    try {
        for (const image of images) {
            const fetched = await fetchPreferredImageBlob(image.sources);
            if (!fetched?.blob) throw new Error('Image unavailable');
            // Keep one decoded original at a time and retain only bounded, resized tiles.
            await withBlobImage(fetched.blob, original => {
                const sourceWidth = original.naturalWidth;
                const sourceHeight = original.naturalHeight;
                if (!sourceWidth || !sourceHeight) throw new Error('Image unavailable');
                const tileWidth = Math.min(2048, sourceWidth);
                const tileHeight = Math.max(1, Math.round(sourceHeight * tileWidth / sourceWidth));
                const nextWidth = Math.max(width, tileWidth);
                const nextHeight = height + tileHeight + (tiles.length ? gap : 0);
                // Check before allocating the next tile or requesting any later images.
                if (nextHeight > 16000 || nextWidth * nextHeight > 24000000) {
                    throw new Error('Combined image too large');
                }
                const tile = document.createElement('canvas');
                tiles.push(tile);
                tile.width = tileWidth;
                tile.height = tileHeight;
                const context = tile.getContext('2d');
                if (!context) throw new Error('Canvas unavailable');
                context.drawImage(original, 0, 0, tileWidth, tileHeight);
                width = nextWidth;
                height = nextHeight;
            });
        }
        canvas = document.createElement('canvas');
        canvas.width = width;
        canvas.height = height;
        const context = canvas.getContext('2d');
        if (!context) throw new Error('Canvas unavailable');
        context.fillStyle = '#ffffff';
        context.fillRect(0, 0, width, height);
        let y = 0;
        tiles.forEach(tile => {
            context.drawImage(tile, Math.round((width - tile.width) / 2), y, tile.width, tile.height);
            y += tile.height + gap;
            tile.width = tile.height = 0;
        });
        return await new Promise((resolve, reject) => canvas.toBlob(blob =>
            blob ? resolve(blob) : reject(new Error('Image encoding failed')), 'image/png'));
    } finally {
        tiles.forEach(tile => { tile.width = tile.height = 0; });
        if (canvas) canvas.width = canvas.height = 0;
    }
};

export const copyCombinedImages = images => {
    if (!navigator.clipboard?.write || typeof ClipboardItem === 'undefined') {
        return Promise.reject(new Error('Image clipboard unavailable'));
    }
    // Start the clipboard write in the click handler; Safari requires user activation.
    const imageBlob = buildCombinedImageBlob(images);
    imageBlob.catch(() => {});
    return navigator.clipboard.write([new ClipboardItem({ 'image/png': imageBlob })]);
};
